import React, { useState, useEffect } from 'react';
import api from '../../api';
import '../HomeBaseForm.css';
import '../Buttons.css';
import './SupplierVarietyManager.css'; // reuse table styling

const SupplierStarterPreorder = ({ currentSupplier, currentDate }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [batches, setBatches] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [batchRefreshTrigger, setBatchRefreshTrigger] = useState(0);

    // populates batch dropdown
    useEffect(() => {
        if (currentSupplier?.id) {
            api.get(`/supplierPreorder/batches/${currentSupplier.id}`)
            .then(res => setBatches(res.data));
        }
    }, [currentSupplier?.id, batchRefreshTrigger]);

    //populates batch details (numbers that were ordered into the recieved column)
    useEffect(() => {
        if (!selectedBatch) return;

        api.get(`/supplierPreorder/batchDetails/${currentSupplier.id}/${selectedBatch}`)
            .then(res => {
                const details = res.data; // array

                setRows(prev =>
                    prev.map(row => {
                        const match = details.find(d => d.supplierPlantID === row.supplierPlantID);
                        return match
                            ? { ...row, supplierReceived: match.quantity }
                            : row;
                    })
                );
            });
    }, [selectedBatch]);


    useEffect(() => {
        if (currentSupplier?.id) {
            console.log("currentSupplier: ", currentSupplier);
            fetchAvailableVarieties();
        }
    }, [currentSupplier?.id]);

    const normalize = (val) => {
        if (val === null || val === undefined || Number(val) === 0) return "";
        return val;
    };

    const fetchAvailableVarieties = async () => {
        try {
            console.log("about to fetchavailable");
            const res = await api.get(`/supplierPreorder/preOrders/${currentSupplier.id}`);

            const available = res.data.map(v => ({
                cropName: v.name,
                plantID: v.plantID,
                supplierPlantID: v.supplierPlantID,
                varietyName: v.varietyName,
                gardenerOrders: v.gardenerOrders,
                gardenerPaid: v.gardenerPaid,
                supplierOrdered: normalize(v.supplierOrdered),
                supplierReceived: normalize(v.supplierReceived),
            }));

            setRows(available);
            setLoading(false);

        } catch (err) {
            console.error("Error fetching available varieties:", err);
            setLoading(false);
        }
    };

    const updateField = (supplierPlantID, field, value) => {
        setRows(prev =>
            prev.map(row =>
                row.supplierPlantID === supplierPlantID
                    ? { ...row, [field]: normalize(value) }
                    : row
            )
        );
    };

    const handleBatchOrders = async () => {
        try {
            const batchName = prompt("Enter a batch name:");
            if (!batchName) return; // user cancelled or empty

            // 1. Detect anomalies
            const anomalies = rows.filter(row =>
                Number(row.gardenerPaid) > 0 &&
                Number(row.supplierOrdered) > 0 &&
                Number(row.gardenerPaid) !== Number(row.supplierOrdered)
            );

            if (anomalies.length > 0) {
                console.warn("Anomalies detected:", anomalies);

                alert(`
Some rows do not match:
- Paid column does not match the supplier Ordered column

These rows cannot be batched.
Please fix them before continuing.
            `);

                return;
            }

            // rows that qualify for batching
        const eligible = rows.filter(row =>
            Number(row.gardenerPaid) === Number(row.supplierOrdered) &&
            Number(row.supplierOrdered) > 0
        );

        if (eligible.length === 0) {
            console.log("No rows eligible for batching.");
            return;
        }

        // send all eligible row to backend
            await api.put('/supplierPreorder/batchOrders', {
                batchName,
                supplierID: currentSupplier.id,
                rows: eligible.map(row => ({
                    supplierPlantID: row.supplierPlantID,
                    gardenerPaid: row.gardenerPaid,
                    supplierOrdered: row.supplierOrdered
                }))
        });

            setSelectedBatch(null);

            await fetchAvailableVarieties(); // refresh UI
            setBatchRefreshTrigger(prev => prev + 1);


    } catch (err) {
        console.error("Error batching orders:", err);
    }
};

    const handleReceiveBatch = async () => {
        try {
            if (!selectedBatch) {
                console.log("No batch selected.");
                return;
            }

            // rows that qualify for receiving
            const eligible = rows.filter(row =>
                Number(row.supplierReceived) > 0
            );

            if (eligible.length === 0) {
                console.log("No rows eligible for receiving.");
                return;
            }

            // send each eligible row to backend
            for (const row of eligible) {

                    await api.put('/supplierPreorder/receiveBatchItem', {
                        supplierID: currentSupplier.id,
                        supplierPlantID: row.supplierPlantID,
                        supplierReceived: row.supplierReceived,
                        batchName: selectedBatch,
                        substitutionNote: row.substitutionNote || null
                    });
            }

            // sweep orders forward
            await api.put('/supplierPreorder/updateReceivedOrders');

            fetchAvailableVarieties(); // refresh UI
            setBatchRefreshTrigger(prev => prev + 1);

        } catch (err) {
            console.error("Error receiving batch:", err);
        }
    };

    if (loading) return <p>Loading preorder data...</p>;

    // GROUP rows by cropName 
    const grouped = rows.reduce((acc, row) => {
        if (!acc[row.cropName]) acc[row.cropName] = [];
        acc[row.cropName].push(row);
        return acc;
    }, {});

    const openNoteEditor = (row) => {
        setRows(prev =>
            prev.map(r =>
                r.supplierPlantID === row.supplierPlantID
                    ? { ...r, showNoteEditor: true }
                    : r
            )
        );
    };

    const nextMonthName = new Date(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        1
    ).toLocaleString('en-US', { month: 'long' });

    return (
        <div className="variety-manager">
            <h2>{nextMonthName} Starter Preorders</h2>

            <div className="preorder-info">
                These are the items currently available for preorder.
                Gardeners are ordering these starters for {nextMonthName}.
            </div>

            <select
                value={selectedBatch || ""}
                onChange={e => setSelectedBatch(e.target.value)}
            >
                <option value="">Select Batch</option>
                {batches.map(b => (
                    <option key={b.batchName} value={b.batchName}>
                        {b.batchName}
                    </option>
                ))}
            </select>

            <table className="variety-table">
                <thead>
                    <tr>
                        <th>Variety</th>
                        <th>Gardener Orders</th>
                        <th>Paid</th>
                        <th>Supplier Ordered</th>
                        <th>Supplier Received</th>
                    </tr>
                </thead>

                <tbody>
                    {Object.entries(grouped).map(([cropName, varieties]) => (
                        <React.Fragment key={cropName}>

                            {/* Crop header row */}
                            <tr className="crop-header">
                                <td colSpan="6" style={{
                                    fontWeight: 'bold',
                                    background: '#eef',
                                    fontSize: '1.1rem'
                                }}>
                                    {cropName}
                                </td>
                            </tr>

                            {/* Variety rows */}
                            {varieties.map(row => (
                                <React.Fragment key={row.supplierPlantID}>
                                  <tr>
                                    <td>{row.varietyName}</td>

                                    <td style={{ textAlign: 'center' }}>{normalize(row.gardenerOrders)}</td>
                                    <td style={{ textAlign: 'center' }}>{normalize(row.gardenerPaid)}</td>

                                    <td>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            min="0"
                                            value={normalize(row.supplierOrdered)}
                                            onChange={e =>
                                                updateField(row.supplierPlantID, 'supplierOrdered', e.target.value)
                                            }
                                        />
                                    </td>

                                        <td style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            <input
                                                type="text"
                                                disabled
                                                value={normalize(row.supplierReceived)}
                                                style={{ width: "60px" }}
                                            />

                                            {row.supplierReceived != null && Number(row.supplierReceived) > 0 && (
                                                <button
                                                    className="small-button"
                                                    onClick={() => openNoteEditor(row)}
                                                >
                                                    Note
                                                </button>
                                            )}
                                        </td>


                                  </tr>
                                    {row.showNoteEditor && (
                                        <tr>
                                            <td colSpan="6">
                                                <textarea
                                                    value={row.substitutionNote || ""}
                                                    onChange={e =>
                                                        updateField(row.supplierPlantID, 'substitutionNote', e.target.value)
                                                    }
                                                    placeholder="Explain shortage or substitution instructions for cashier"
                                                    style={{ width: "100%", minHeight: "60px" }}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>

                            ))}

                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            <div style={{ marginTop: '1rem' }}>
                <button className="button" onClick={handleBatchOrders} >
                    Batch Orders
                </button>
            </div>

            <div style={{ marginTop: '1rem' }}>
                <button className="button" onClick={handleReceiveBatch} >
                    Receive Batch
                </button>
            </div>

        </div>
    );
};

export default SupplierStarterPreorder;
